"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = Toolbar;
var _list = require("@lexical/list");
var _LexicalComposerContext = require("@lexical/react/LexicalComposerContext");
var _richText = require("@lexical/rich-text");
var _selection = require("@lexical/selection");
var _utils = require("@lexical/utils");
var _ExpandMoreRounded = _interopRequireDefault(require("@mui/icons-material/esm/ExpandMoreRounded.js"));
var _FormatBoldRounded = _interopRequireDefault(require("@mui/icons-material/esm/FormatBoldRounded.js"));
var _FormatItalicRounded = _interopRequireDefault(require("@mui/icons-material/esm/FormatItalicRounded.js"));
var _FormatListBulletedRounded = _interopRequireDefault(require("@mui/icons-material/esm/FormatListBulletedRounded.js"));
var _FormatListNumberedRounded = _interopRequireDefault(require("@mui/icons-material/esm/FormatListNumberedRounded.js"));
var _FormatUnderlinedRounded = _interopRequireDefault(require("@mui/icons-material/esm/FormatUnderlinedRounded.js"));
var _StrikethroughSRounded = _interopRequireDefault(require("@mui/icons-material/esm/StrikethroughSRounded.js"));
var _Title = _interopRequireDefault(require("@mui/icons-material/esm/Title.js"));
var _Box = _interopRequireDefault(require("@mui/material/Box/Box.js"));
var _ClickAwayListener = require("@mui/base/ClickAwayListener");
var _Divider = _interopRequireDefault(require("@mui/material/Divider/Divider.js"));
var _Grow = _interopRequireDefault(require("@mui/material/Grow/Grow.js"));
var _ListItemButton = _interopRequireDefault(require("@mui/material/ListItemButton/ListItemButton.js"));
var _ListItemIcon = _interopRequireDefault(require("@mui/material/ListItemIcon/ListItemIcon.js"));
var _ListItemText = _interopRequireDefault(require("@mui/material/ListItemText/ListItemText.js"));
var _MenuItem = _interopRequireDefault(require("@mui/material/MenuItem/MenuItem.js"));
var _MenuList = _interopRequireDefault(require("@mui/material/MenuList/MenuList.js"));
var _Paper = _interopRequireDefault(require("@mui/material/Paper/Paper.js"));
var _Popper = _interopRequireDefault(require("@mui/material/Popper/Popper.js"));
var _ToggleButton = _interopRequireDefault(require("@mui/material/ToggleButton/ToggleButton.js"));
var _ToggleButtonGroup = _interopRequireDefault(require("@mui/material/ToggleButtonGroup/ToggleButtonGroup.js"));
var _Typography = _interopRequireDefault(require("@mui/material/Typography/Typography.js"));
var _lexical = require("lexical");
var _react = require("react");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const formatOptions = [{
  label: 'Bold (ctrl+B)',
  value: 'bold',
  Icon: _FormatBoldRounded.default
}, {
  label: 'Italic',
  value: 'italic',
  Icon: _FormatItalicRounded.default
}, {
  label: 'Underline',
  value: 'underline',
  Icon: _FormatUnderlinedRounded.default
}, {
  label: 'Strikethrough',
  value: 'strikethrough',
  Icon: _StrikethroughSRounded.default
}];
const handleBlockTypeChange = createNode => editor => {
  editor.update(() => {
    const selection = (0, _lexical.$getSelection)();
    if ((0, _lexical.$isRangeSelection)(selection)) {
      (0, _selection.$setBlocksType)(selection, createNode);
    }
  });
};
const handleListCommand = command => editor => {
  editor.dispatchCommand(command, null);
};
const blockTypeOptions = {
  paragraph: {
    label: 'Normal',
    value: 'paragraph',
    Icon: _Title.default,
    onSelect: handleBlockTypeChange(_lexical.$createParagraphNode)
  },
  h1: {
    label: 'Large Heading',
    value: 'h1',
    Icon: ({
      sx
    }) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
      sx: {
        ...sx,
        fontSize: 16,
        fontWeight: 600
      },
      children: "H1"
    }),
    onSelect: handleBlockTypeChange(() => (0, _richText.$createHeadingNode)('h1'))
  },
  h2: {
    label: 'Small Heading',
    value: 'h2',
    Icon: ({
      sx
    }) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
      sx: {
        ...sx,
        fontSize: 14,
        fontWeight: 600
      },
      children: "H2"
    }),
    onSelect: handleBlockTypeChange(() => (0, _richText.$createHeadingNode)('h2'))
  },
  ol: {
    label: 'Ordered List',
    value: 'ol',
    Icon: _FormatListNumberedRounded.default,
    onSelect: handleListCommand(_list.INSERT_ORDERED_LIST_COMMAND)
  },
  ul: {
    label: 'Bullet List',
    value: 'ul',
    Icon: _FormatListBulletedRounded.default,
    onSelect: handleListCommand(_list.INSERT_UNORDERED_LIST_COMMAND)
  }
};
function Toolbar() {
  const [editor] = (0, _LexicalComposerContext.useLexicalComposerContext)();
  const [anchorEl, setAnchorEl] = (0, _react.useState)(null);
  const [blockType, setBlockType] = (0, _react.useState)(blockTypeOptions.paragraph);
  const [formats, setFormats] = (0, _react.useState)([]);
  const updateToolbar = (0, _react.useCallback)(() => {
    const selection = (0, _lexical.$getSelection)();
    if ((0, _lexical.$isRangeSelection)(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ((0, _list.$isListNode)(element)) {
          const parentList = (0, _utils.$getNearestNodeOfType)(anchorNode, _list.ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          blockTypeOptions[type] && setBlockType(blockTypeOptions[type]);
        } else {
          const type = (0, _richText.$isHeadingNode)(element) ? element.getTag() : element.getType();
          blockTypeOptions[type] && setBlockType(blockTypeOptions[type]);
        }
      }
      // Update text format
      const formats = Object.values(formatOptions).reduce((formats, {
        value
      }) => {
        if (selection.hasFormat(value)) formats.push(value);
        return formats;
      }, []);
      setFormats(formats);
    }
  }, [editor]);
  (0, _react.useEffect)(() => {
    return (0, _utils.mergeRegister)(editor.registerUpdateListener(({
      editorState
    }) => editorState.read(updateToolbar)), editor.registerCommand(_lexical.SELECTION_CHANGE_COMMAND, () => {
      updateToolbar();
      return false;
    }, 1));
  }, [editor, updateToolbar]);
  const openBlockTypeMenu = e => setAnchorEl(e.currentTarget);
  const closeBlockTypeMenu = () => setAnchorEl(null);
  const handleFormatChange = (e, format) => editor.dispatchCommand(_lexical.FORMAT_TEXT_COMMAND, format);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Box.default, {
    sx: {
      px: 1,
      display: 'flex',
      flexFlow: 'row wrap',
      alignItems: 'center',
      borderBottom: 2,
      borderColor: 'border.main'
    },
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_ListItemButton.default, {
      dense: true,
      disableGutters: true,
      color: "inherit",
      onClick: openBlockTypeMenu,
      sx: {
        px: 1,
        borderRadius: 1,
        flex: '0 0 auto',
        width: 150
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_ListItemIcon.default, {
        sx: {
          minWidth: 24,
          mr: 1,
          color: 'inherit'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(blockType.Icon, {})
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ListItemText.default, {
        primary: blockType.label
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ExpandMoreRounded.default, {
        sx: {
          ml: 'auto'
        }
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Popper.default, {
      open: Boolean(anchorEl),
      anchorEl: anchorEl,
      transition: true,
      style: {
        zIndex: '1300'
      },
      children: ({
        TransitionProps
      }) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Grow.default, {
        ...TransitionProps,
        style: {
          transformOrigin: 'center top'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Paper.default, {
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_ClickAwayListener.ClickAwayListener, {
            onClickAway: closeBlockTypeMenu,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_MenuList.default, {
              children: Object.values(blockTypeOptions).map(({
                label,
                value,
                Icon,
                onSelect
              }) => /*#__PURE__*/(0, _jsxRuntime.jsxs)(_MenuItem.default, {
                sx: {
                  py: 1,
                  px: 2
                },
                selected: value === blockType.value,
                onClick: () => {
                  onSelect(editor);
                  setBlockType(blockTypeOptions[value]);
                  closeBlockTypeMenu();
                },
                children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(Icon, {
                  sx: {
                    mr: 1
                  }
                }), label]
              }, value))
            })
          })
        })
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Divider.default, {
      flexItem: true,
      sx: {
        m: 1,
        borderColor: 'text.primary'
      },
      orientation: "vertical"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ToggleButtonGroup.default, {
      sx: {
        flex: 1,
        py: 0.5,
        '& .MuiToggleButtonGroup-grouped': {
          mx: 0.5,
          border: 0,
          '&:not(:first-of-type)': {
            borderRadius: 1
          },
          '&:first-of-type': {
            borderRadius: 1
          }
        }
      },
      value: formats,
      children: formatOptions.map(({
        label,
        value,
        Icon
      }) => /*#__PURE__*/(0, _jsxRuntime.jsx)(_ToggleButton.default, {
        sx: {
          p: 1,
          border: 0,
          color: 'text.primary'
        },
        value: value,
        "aria-label": label,
        onClick: handleFormatChange,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(Icon, {})
      }, value))
    })]
  });
}